import * as React from 'react'
import { TreeManager } from '@basehub/utils/tree/index'
import { invariant } from '@epic-web/invariant'

import {
  type Block,
  type ComponentBlock,
  type InstanceBlock,
  type ListBlock,
  isComponentBlock,
  isInstanceBlock,
  type ListView,
  type CommonBlocksCompoundIndexes,
} from '@basehub/data/replicache/index'
import { useSelectedView } from './views/use-selected-view'
import { BaseHubCollectionColumn } from './views/table/types'
import { filter } from './helpers/filter'
import { CheckIcon, DotsHorizontalIcon } from '@radix-ui/react-icons'
import { IconButton } from '~/sotano-kit/primitives/icon-button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/sotano-kit/primitives/dropdown-menu'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { getId } from '@basehub/utils/id/index'
import { clsx } from 'clsx'
import { getBlockFromColumnId } from '@basehub/data/replicache/schema/block/list/utils'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { resolveVisibleColumns } from './views/table/columns'
import { useTreeSlice } from '~/hooks/use-tree-slice'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useCustomSyncerSub } from '~/replicache/frontend/use-custom-syncer-sub'
import { compare, getSortValues } from '@basehub/data/db/list-helpers/sort'
import { useStorage } from '~/hooks/use-storage'

export type ListBlockRow = InstanceBlock | ComponentBlock

export const useListTable = ({
  block,
  treeManager,
  bypassFilterForRowIds,
}: {
  block: ListBlock | undefined | null
  treeManager: TreeManager<Block, CommonBlocksCompoundIndexes> | null
  bypassFilterForRowIds?: 'all' | Array<Block['id']> | null
}):
  | {
      rows: never[]
      columns: never[]
      listTemplate?: never
      length: number
      rowsShown?: never
      loadMore: () => void
      allRowsShown: true
      isLoading: boolean
    }
  | {
      rows: Array<ListBlockRow>
      columns: Array<BaseHubCollectionColumn>
      listTemplate: ComponentBlock
      length: number
      rowsShown: number
      loadMore: () => void
      allRowsShown: boolean
      isLoading: boolean
    } => {
  const rootBlockId = treeManager?.root.value.id
  const [selectedView] = useSelectedView(block)
  const prevProcessedRowsLengthRef = React.useRef<number>(0)

  const listChildren = useTreeSlice(treeManager, (s) => {
    const childrenKeys = block?.id
      ? s.getChildrenKeys(idToSourceBlockId(block))
      : null
    return (
      childrenKeys?.map((id) => s.getNode(id, { required: true }).value) ?? []
    )
  })

  // 2. Cache the template block lookup
  const templateBlockNode = React.useMemo(() => {
    if (!treeManager) return null
    if (!block?.typeOptions.targetBlockId) return null

    const _templateBlock =
      treeManager.getNodeBy('sourceBlockId', block.typeOptions.targetBlockId) ??
      treeManager.getNode(block.typeOptions.targetBlockId)

    if (!_templateBlock) return null

    invariant(
      isComponentBlock(_templateBlock.value),
      'Template block is not a component block'
    )

    return _templateBlock
  }, [treeManager, block?.typeOptions.targetBlockId])

  const templateBlock = templateBlockNode?.value as undefined | ComponentBlock

  // 3. Cache the basic rows array
  const baseRows = React.useMemo(() => {
    const listEntries: ListBlockRow[] = []

    for (const listEntry of listChildren ?? []) {
      if (
        isInstanceBlock(listEntry) ||
        (isComponentBlock(listEntry) && !listEntry.hidden)
      ) {
        listEntries.push(listEntry)
      }
    }

    return listEntries
  }, [listChildren])

  const listTemplateSourceBlockId = templateBlock
    ? idToSourceBlockId(templateBlock)
    : null

  const columns =
    useTreeSlice(
      treeManager,
      (tm) => {
        if (!listTemplateSourceBlockId) return []
        return resolveVisibleColumns({
          listTemplateId: listTemplateSourceBlockId,
          selectedView,
          treeManager: tm,
        })
      },
      'reference',
      [listTemplateSourceBlockId, selectedView]
    ) ?? []

  const replicache = useRepoReplicache()?.data?.replicache
  const showOnFirstLoad = selectedView?.showOnFirstLoad ?? 10
  const pageSize = showOnFirstLoad
  const [page, setPage] = useStorage(
    'sessionStorage',
    (z) => z.number(),
    block ? idToSourceBlockId(block) : '',
    {
      initialValue: 1,
      enabled: !!block,
    }
  )

  const { data, isPreviousData } = useCustomSyncerSub(
    rootBlockId && block && selectedView && page !== undefined
      ? replicache
      : undefined,
    {
      syncer: 'loadListView',
      params: {
        page: page!,
        pageSize,
        sourceBlockId: block ? idToSourceBlockId(block) : '',
        selectedView: selectedView as ListView,
        rootBlockId: rootBlockId!,
      },
      keepPreviousData: true,
    }
  )
  const isLoading = !data || isPreviousData

  // 5. Cache filtered & sorted rows separately
  const processedRows = React.useMemo(() => {
    if (!selectedView || !treeManager || !templateBlock) {
      return baseRows
    }

    let rows = baseRows

    // Only applies filters if has any
    if (bypassFilterForRowIds !== 'all' && selectedView.filter.length) {
      const getBlock = (id: string) => treeManager?.getNode(id)?.value
      const getChildrenLength = (id: string) =>
        (treeManager?.getChildrenKeys(id) ?? []).length

      rows = rows.filter((entry) => {
        if (bypassFilterForRowIds?.includes(entry.id)) return true

        return selectedView.filter.every((f) => {
          if (f.type === 'and' || f.type === 'or') {
            throw new Error('and/or not supported yet')
          }

          if (f.by === '__title') {
            return filter({
              block: { type: 'text', value: entry.title },
              filter: f,
              getBlock,
              getChildrenLength,
            })
          }

          if (f.by === '__hidden') {
            return filter({
              block: { type: 'boolean', value: entry.hidden ?? false },
              filter: f,
              getBlock,
              getChildrenLength,
            })
          }

          const { block: filterBlock } = getBlockFromColumnId(f.by, {
            row: entry,
            treeManager: treeManager,
          })

          if (!filterBlock) return false
          const block = treeManager.getNode(filterBlock.id)?.value
          if (!block) return false

          return filter({
            block,
            filter: f,
            getBlock,
            getChildrenLength,
          })
        })
      })
    }

    // Only sort if needed
    if (selectedView.sort.length) {
      rows = [...rows].sort((a, b) => {
        const aBrOrder = treeManager?.getNode(a.id)?.order || ''
        const bBrOrder = treeManager?.getNode(b.id)?.order || ''

        const aSortValues = getSortValues({
          block: a,
          brOrder: aBrOrder,
          sort: selectedView.sort,
          getBlockByInstanceChildTarget: (instanceChildTarget) => {
            for (const childId of treeManager.getChildrenKeys(a.id)) {
              const child = treeManager.getNode(childId)?.value
              if (child?.instanceChildTarget === instanceChildTarget) {
                return child
              }
            }
            return null
          },
        })

        const bSortValues = getSortValues({
          block: b,
          brOrder: bBrOrder,
          sort: selectedView.sort,
          getBlockByInstanceChildTarget: (instanceChildTarget) => {
            for (const childId of treeManager.getChildrenKeys(b.id)) {
              const child = treeManager.getNode(childId)?.value
              if (child?.instanceChildTarget === instanceChildTarget) {
                return child
              }
            }
            return null
          },
        })

        return compare(aSortValues, bSortValues)
      })
    }

    return rows
  }, [
    selectedView,
    treeManager,
    baseRows,
    bypassFilterForRowIds,
    templateBlock,
  ])

  React.useEffect(() => {
    const rows =
      processedRows.length > prevProcessedRowsLengthRef.current
        ? processedRows.length
        : prevProcessedRowsLengthRef.current

    prevProcessedRowsLengthRef.current = rows
  }, [processedRows?.length])

  const rowsDeleted = prevProcessedRowsLengthRef?.current
    ? prevProcessedRowsLengthRef?.current - processedRows.length
    : 0
  const rowsShown = pageSize * (page ?? 0)
  const length = (data?.total ?? processedRows.length) - rowsDeleted

  // 6. Handle pagination at the end
  const finalRows = React.useMemo(() => {
    return processedRows.slice(0, rowsShown)
  }, [processedRows, rowsShown])

  if (!templateBlock) {
    return {
      rows: [],
      columns: [],
      listTemplate: undefined,
      length: 0,
      rowsShown: undefined,
      loadMore: () => {},
      allRowsShown: true,
      isLoading,
    }
  }

  return {
    rows: finalRows,
    columns,
    listTemplate: templateBlock,
    length,
    allRowsShown: finalRows.length >= length,
    rowsShown: finalRows.length,
    loadMore: () => {
      if (isLoading) return
      setPage((p) => {
        return (p ?? 1) + 1
      })
    },
    isLoading,
  }
}

export type UseListTableResult = ReturnType<typeof useListTable>

export const LoadMoreItemsButton = ({
  block,
  children,
  ...rest
}: {
  block: ListBlock
  children: React.ReactNode
} & React.JSX.IntrinsicElements['span']) => {
  const [selectedView] = useSelectedView(block)

  return (
    <span
      {...rest}
      tabIndex={0}
      className={clsx(
        'z-30 focus-visible:outline focus:outline outline-dark-control-base',
        selectedView?.layout === 'inline'
          ? 'rounded-md focus-visible:outeline-dark-control-base bg-dark-border-solid focus-visible:bg-dark-shade-hover hover:bg-dark-shade-hover font-medium text-xs text-dark-muted border-dark-border-solid cursor-default items-center justify-center px-2 py-1 inline-flex'
          : 'flex h-9 w-full select-none bg-dark-base border-b focus-visible:bg-dark-shade border-dark-border-solid -outline-offset-1 cursor-default items-center justify-start rounded-none px-2 py-1.5 text-xs text-dark-faint hover:bg-dark-shade'
      )}
    >
      {children}
    </span>
  )
}

export const ShowOnFirstLoadMenu = ({
  block,
  path,
}: {
  block: ListBlock
  path: string
}) => {
  const [selectedView] = useSelectedView(block)
  const replicache = useRepoReplicache()?.data?.replicache
  const treeManager = useWorkingTreeStore((s) => s.tree)

  const handleUpdateShowOnFirstLoad = async (
    value: ListView['showOnFirstLoad']
  ) => {
    if (!replicache || !treeManager) return

    await replicache.mutate.updateListView({
      blockId: block.id,
      targetBlockId_withinTree: treeManager.getNode(
        block.typeOptions.targetBlockId,
        { required: true }
      ).value.id,
      id: selectedView?.id,
      idSeed: getId(),
      data: { showOnFirstLoad: value },
      isoNow: new Date().toISOString(),
      path,
    })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
        <IconButton
          onClick={(e) => e.stopPropagation()}
          className="hover:text-dark-dim"
        >
          <DotsHorizontalIcon />
        </IconButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <p className="font-medium text-dark-muted text-xs mb-1 p-1">
          Show on first load
        </p>
        <DropdownMenuItem
          onSelect={(e) => {
            e.stopPropagation()
            handleUpdateShowOnFirstLoad(10)
          }}
        >
          10
          {(selectedView?.showOnFirstLoad ?? 10) === 10 && (
            <CheckIcon className="ml-auto" />
          )}
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={(e) => {
            e.stopPropagation()
            handleUpdateShowOnFirstLoad(25)
          }}
        >
          25
          {selectedView?.showOnFirstLoad === 25 && (
            <CheckIcon className="ml-auto" />
          )}
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={(e) => {
            e.stopPropagation()
            handleUpdateShowOnFirstLoad(50)
          }}
        >
          50
          {selectedView?.showOnFirstLoad === 50 && (
            <CheckIcon className="ml-auto" />
          )}
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={(e) => {
            e.stopPropagation()
            handleUpdateShowOnFirstLoad(100)
          }}
        >
          100
          {selectedView?.showOnFirstLoad === 100 && (
            <CheckIcon className="ml-auto" />
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
